import { BehaviorSubject } from 'rxjs';
import { MapMouseEvent } from 'mapbox-gl';
import { arePointsEqual } from '@shared/helpers/geometry/geometry.helpers';
import { Point2D } from '@shared/models/geometry.model';
import { Coordinate } from '@shared/models/geography.model';
import { MapHoverState, MapHoverStateType, PointPayload } from '../models';

type HoverControllerDependencies = {
  hoverState$: BehaviorSubject<MapHoverState | undefined>;
  getStoreyAdjustedScreenPoint: (event: MapMouseEvent) => Point2D | undefined;
  onHoverStateChange: () => void;
};

export type LineHoverHit = {
  shapeId: string;
  startIndex: number;
  endIndex: number;
  screenPoint: Point2D;
  lengthMeters: number;
  midpoint: Point2D;
  midpointCoordinate?: Coordinate;
};

export type DraftLineHoverHit = {
  startIndex: number;
  endIndex: number;
  screenPoint: Point2D;
};

/**
 * Encapsulates hover state transitions so MapEventHandler stays readable.
 */
export class MapHoverController {
  private lineInsertPointHovered = false;

  constructor(private readonly deps: HoverControllerDependencies) {}

  applyPointHoverState(payload: PointPayload): void {
    if (payload.isDraft) {
      const shapeId =
        payload.shapeId ??
        (typeof payload.shape.id === 'string' ? payload.shape.id : undefined);
      const canComplete =
        payload.index === 0 &&
        payload.shape.points &&
        payload.shape.points.length >= 3;
      this.setHoverState({
        type: MapHoverStateType.DraftPoint,
        index: payload.index,
        shapeId,
        canComplete,
        screenPoint: payload.screenPoint,
      });
      return;
    }
    const shapeId =
      payload.shapeId ??
      (typeof payload.shape.id === 'string' ? payload.shape.id : undefined);
    if (!shapeId) {
      this.clearHoverState(MapHoverStateType.Point);
      return;
    }
    this.setHoverState({
      type: MapHoverStateType.Point,
      index: payload.index,
      shapeId,
      screenPoint: payload.screenPoint,
    });
  }

  activateLineInsertCandidateHover(
    event: MapMouseEvent,
    hasPointInsertCapability: boolean,
  ): void {
    if (!hasPointInsertCapability) return;
    this.lineInsertPointHovered = true;
    const current = this.deps.hoverState$.getValue();
    if (current?.type !== MapHoverStateType.Line) return;
    const screenPoint =
      this.deps.getStoreyAdjustedScreenPoint(event) ?? current.screenPoint;
    this.setHoverState({
      ...current,
      insertCandidateActive: true,
      screenPoint,
    });
  }

  deactivateLineInsertCandidateHover(): void {
    if (!this.lineInsertPointHovered) return;
    this.lineInsertPointHovered = false;
    const current = this.deps.hoverState$.getValue();
    if (
      !current ||
      current.type !== MapHoverStateType.Line ||
      !current.insertCandidateActive
    ) {
      return;
    }
    this.setHoverState({
      ...current,
      insertCandidateActive: false,
    });
  }

  buildLineHoverState(
    hit: LineHoverHit | undefined,
    hasPointInsertCapability: boolean,
  ): MapHoverState | undefined {
    if (!hasPointInsertCapability || !hit) return undefined;
    // Preserve insertCandidateActive from current state when the insert
    // candidate is actively hovered; otherwise default to false so the
    // flag clears immediately when leaving the candidate dot.
    const current = this.deps.hoverState$.getValue();
    const preserveActive =
      this.lineInsertPointHovered &&
      current?.type === MapHoverStateType.Line &&
      current.insertCandidateActive;
    return {
      type: MapHoverStateType.Line,
      shapeId: hit.shapeId,
      startIndex: hit.startIndex,
      endIndex: hit.endIndex,
      screenPoint: hit.screenPoint,
      lengthMeters: hit.lengthMeters,
      midpoint: hit.midpoint,
      midpointCoordinate: hit.midpointCoordinate,
      insertCandidateActive: Boolean(preserveActive),
    };
  }

  /**
   * Draft line should never produce a hover state.
   * Method kept for API compatibility with existing callers.
   */
  buildDraftLineHoverState(
    _hit: DraftLineHoverHit | undefined,
  ): MapHoverState | undefined {
    // Explicitly disable draft line hover semantics
    return undefined;
  }

  buildShapeHoverState(
    shapeId: string | undefined,
    screenPoint: Point2D | undefined,
  ): MapHoverState | undefined {
    if (!shapeId || !screenPoint) return undefined;
    return {
      type: MapHoverStateType.Shape,
      shapeId,
      screenPoint,
    };
  }

  hasPointHoverPriority(): boolean {
    const hoverState = this.deps.hoverState$.getValue();
    if (!hoverState) return false;
    return (
      hoverState.type === MapHoverStateType.Point ||
      hoverState.type === MapHoverStateType.DraftPoint
    );
  }

  /**
   * True when current hover is a line segment (selected storey).
   * Used so shape hover does not overwrite line hover when storey planes overlap.
   */
  hasLineHoverPriority(): boolean {
    const hoverState = this.deps.hoverState$.getValue();
    return hoverState?.type === MapHoverStateType.Line;
  }

  setHoverState(state: MapHoverState | undefined): void {
    if (!state) {
      this.clearHoverState();
      return;
    }
    if (state.type !== MapHoverStateType.Line) {
      this.lineInsertPointHovered = false;
    }
    const current = this.deps.hoverState$.getValue();
    const currentScreenPoint =
      current && 'screenPoint' in current ? current.screenPoint : undefined;
    const nextScreenPoint =
      'screenPoint' in state ? state.screenPoint : undefined;
    if (
      this.areHoverStatesEqual(current, state) &&
      arePointsEqual(currentScreenPoint, nextScreenPoint)
    ) {
      return;
    }
    this.deps.hoverState$.next(state);
    this.deps.onHoverStateChange();
  }

  clearHoverState(type?: MapHoverStateType): void {
    const current = this.deps.hoverState$.getValue();
    if (!current) return;
    if (type && current.type !== type) return;
    if (!type || type === MapHoverStateType.Line) {
      this.lineInsertPointHovered = false;
    }
    this.deps.hoverState$.next(undefined);
    this.deps.onHoverStateChange();
  }

  private areHoverStatesEqual(
    current: MapHoverState | undefined,
    next: MapHoverState | undefined,
  ): boolean {
    if (!current && !next) return true;
    if (!current || !next) return false;
    if (current.type !== next.type) return false;
    switch (current.type) {
      case MapHoverStateType.Point:
        return (
          next.type === MapHoverStateType.Point &&
          current.index === next.index &&
          current.shapeId === next.shapeId
        );
      case MapHoverStateType.DraftPoint:
        return (
          next.type === MapHoverStateType.DraftPoint &&
          current.index === next.index &&
          current.shapeId === next.shapeId &&
          current.canComplete === next.canComplete
        );
      case MapHoverStateType.Line:
        return (
          next.type === MapHoverStateType.Line &&
          current.shapeId === next.shapeId &&
          current.startIndex === next.startIndex &&
          current.endIndex === next.endIndex &&
          Boolean(current.insertCandidateActive) ===
            Boolean(next.insertCandidateActive)
        );
      case MapHoverStateType.DraftLine:
        return (
          next.type === MapHoverStateType.DraftLine &&
          current.startIndex === next.startIndex &&
          current.endIndex === next.endIndex
        );
      case MapHoverStateType.Shape:
        return (
          next.type === MapHoverStateType.Shape &&
          current.shapeId === next.shapeId
        );
      default:
        return false;
    }
  }
}
